import React, { useState, useEffect, useContext, useMemo, Suspense, lazy } from 'react';
import {
  Grid,
  Paper,
} from '@mui/material';
import { gridSpacing } from '../../common/themes/constants';
import { Redirect } from 'react-router-dom';
import TotalReferralPerMonth from './TotalReferralPerMonth';
import TotalReferralPreviousMonth from './TotalReferralPreviousMonth';
import TotalReferralsByMonth from './TotalReferralsByMonth';
import AuthContext from '../auth/context/AuthContext';
import NavigationManager from '../common/services/NavigationManager';
import { isEmpty } from 'lodash';
import { useQuery } from '@apollo/client';
import clientGraphql from '../../common/apollo-graphql';
import { GET_ADMIN_REFERRAL_COUNTS_FOR_DASHBOARD } from '../referrals/gql';
import { GET_ADMIN_DASHBOARD_METRICS } from './gql/Query';
import SectionSkeleton from './SectionSkeleton';

const TotalWaitingTimeDashboard = lazy(() => import('./TotalWaitingTimeDashboard'));
const ReferralReceivedByMonthAndYear = lazy(() => import('./ReferralReceivedByMonthAndYear'));
const ReferralRejectedByMonthAndYear = lazy(() => import('./ReferralRejectedByMonthAndYear'));
const TotalReceivedPerClinicByMonthAndYear = lazy(() => import('./TotalReceivedPerClinicByMonthAndYear'));

export default function Dashboard() {
  const [isLoading, setLoading] = useState(true);
  const currentUser = useContext(AuthContext);
  const redirectTo = NavigationManager.redirectTo(currentUser);
  const Panel = ({ children }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        backgroundColor: 'background.paper',
      }}
    >
      {children}
    </Paper>
  );

  useEffect(() => {
    setLoading(false);
  }, []);

  const { data: dashboardMetricsData } = useQuery(GET_ADMIN_DASHBOARD_METRICS, {
    client: clientGraphql,
    variables: { daysInterval: 5 },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const { currentMonth, currentYear, previousMonth, previousYear } = useMemo(() => {
    const now = new Date();
    const thisMonth = now.getMonth() + 1;
    const thisYear = now.getFullYear();
    const prevMonth = thisMonth === 1 ? 12 : thisMonth - 1;
    const prevYear = thisMonth === 1 ? thisYear - 1 : thisYear;
    return {
      currentMonth: String(thisMonth),
      currentYear: String(thisYear),
      previousMonth: String(prevMonth),
      previousYear: String(prevYear),
    };
  }, []);

  const { data: countsData, loading: loadingCounts } = useQuery(GET_ADMIN_REFERRAL_COUNTS_FOR_DASHBOARD, {
    client: clientGraphql,
    variables: {
      currentMonth,
      currentYear,
      previousMonth,
      previousYear,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const currentCount = countsData?.current?.[0]?.count ?? 0;
  const previousCount = countsData?.previous?.[0]?.count ?? 0;
  const cardLoading = isLoading || loadingCounts;
  const chartFallback = <SectionSkeleton height={500} />;
  const tallChartFallback = <SectionSkeleton height={1000} />;

  return (
    <>
      {!isEmpty(currentUser) && !isEmpty(currentUser.role) && currentUser.role.toUpperCase() === 'ADMIN' ? (
        <Grid container
          spacing={gridSpacing}
          className="dashboard-dashboard">
          <Grid item xs={12}>
            <Grid container spacing={gridSpacing}>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Grid container spacing={gridSpacing}>
                  <Grid item sm={6} xs={6} md={6} lg={6}>
                    <TotalReferralPerMonth
                      isLoading={cardLoading}
                      month={currentMonth}
                      year={currentYear}
                      count={currentCount}
                    />
                  </Grid>
                  <Grid item sm={6} xs={6} md={6} lg={6}>
                    <TotalReferralPreviousMonth
                      isLoading={cardLoading}
                      month={previousMonth}
                      year={previousYear}
                      count={previousCount}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={gridSpacing}>
              <Grid item xs={12} md={12}>
                <Panel>
                  <TotalReferralsByMonth isLoading={isLoading} />
                </Panel>
              </Grid>
              <Grid item xs={12} md={12}>
                <Panel>
                  <Suspense fallback={chartFallback}>
                    <TotalWaitingTimeDashboard data={dashboardMetricsData ?? null} />
                  </Suspense>
                </Panel>
              </Grid>
              <Grid item xs={12} md={12}>
                <Panel>
                  <Suspense fallback={chartFallback}>
                    <ReferralReceivedByMonthAndYear data={dashboardMetricsData ?? null} />
                  </Suspense>
                </Panel>
              </Grid>
              <Grid item xs={12} md={12}>
                <Panel>
                  <Suspense fallback={chartFallback}>
                    <ReferralRejectedByMonthAndYear data={dashboardMetricsData ?? null} />
                  </Suspense>
                </Panel>
              </Grid>
              <Grid item xs={12} md={12}>
                <Panel>
                  <Suspense fallback={tallChartFallback}>
                    <TotalReceivedPerClinicByMonthAndYear data={dashboardMetricsData ?? null} />
                  </Suspense>
                </Panel>
              </Grid>
            </Grid>
          </Grid>
        </Grid>) : <Redirect to={redirectTo} />}
    </>

  );
};
